/**
 * Elasticsearch search DSL dialect + compiler.
 *
 * The reference dialect for the search family — OpenSearch extends it. The
 * shared flow lives in `SearchDslCompiler`; this dialect provides the
 * Elasticsearch query fragments:
 *   - term:           { term: { <field>: <value> } }
 *   - range:          { range: { <field>: { gte: <value> } } }
 *   - wildcard:       { wildcard: { <field>: '*value*' } }
 *   - terms agg:      { terms: { field, size } }
 *   - date histogram: { date_histogram: { field, calendar_interval, offset } }
 *   - search_after:   { search_after: cursor }
 */
import {
    SearchDslCompiler,
    esFieldName,
    type SearchDslDialect,
} from './search-dsl-compiler';
import type { FieldRefNode, GroupFieldNode } from '../../plan/query-plan';

export class ElasticsearchDialect implements SearchDslDialect {
    readonly engine: 'elasticsearch' | 'opensearch' = 'elasticsearch';

    renderTerm(field: FieldRefNode, value: unknown): Record<string, unknown> {
        return { term: { [esFieldName(field)]: value } };
    }

    renderRange(
        field: FieldRefNode,
        bounds: Partial<Record<'gt' | 'gte' | 'lt' | 'lte', unknown>>,
    ): Record<string, unknown> {
        return { range: { [esFieldName(field)]: bounds } };
    }

    renderWildcard(
        field: FieldRefNode,
        pattern: string,
    ): Record<string, unknown> {
        return { wildcard: { [esFieldName(field)]: pattern } };
    }

    renderTermsAggregation(group: GroupFieldNode): Record<string, unknown> {
        const terms: Record<string, unknown> = {
            field: esFieldName(group.field),
        };
        if (group.limit != null) {
            terms.size = group.limit;
        }
        return { terms };
    }

    renderDateHistogram(group: GroupFieldNode): Record<string, unknown> {
        const dateHistogram: Record<string, unknown> = {
            field: esFieldName(group.field),
            calendar_interval: group.granularity,
        };
        if (group.offset) {
            dateHistogram.offset = group.offset;
        }
        return { date_histogram: dateHistogram };
    }

    renderSearchAfter(cursor: unknown[]): Record<string, unknown> {
        return { search_after: cursor };
    }
}

export class ElasticsearchCompiler extends SearchDslCompiler {
    constructor() {
        super(new ElasticsearchDialect());
    }
}
